import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import type { SavedPrompt } from '@/models/SavedPrompt'
import { useChatbotStore } from '@/stores/chatbot'
import { useProjectsStore } from '@/stores/projects'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'

/**
 * Put a saved prompt's body into the chat input (MYAC-1106).
 *
 * The draft lives in `chatbotStore.draftMessage` (survives panel close/reopen,
 * MYAC-750). A prompt never silently overwrites text the user typed: when the
 * draft is non-empty and different, a confirm dialog asks first. Nothing is
 * sent — the user still presses send.
 */
export function useSavedPromptPrefill() {
  const chatbotStore = useChatbotStore()
  const confirm = useConfirm()
  const { t } = useI18n()

  const apply = (prompt: SavedPrompt) => {
    chatbotStore.draftMessage = prompt.body
    // MYAC-1138: deliberately NO meta — a saved prompt's name or body can
    // describe an internal or client-specific procedure. A per-tenant count
    // is what CSM needs, fired only once the prompt is actually applied to
    // the draft (not on a confirm dialog that the user may still cancel).
    try {
      // Fix-wave (whole-branch review, C1): `chatbotStore.projectId` is only ever
      // set once a message is sent, so a saved prompt reused before the first send
      // (e.g. right after opening the dock) read null here — fall back to the
      // currently selected project, same idiom as chatbot.ts.
      trackAction(CsmAction.ChatPromptReused, {
        projectId: chatbotStore.projectId || useProjectsStore().currentProjectId || undefined,
      })
    } catch { }
  }

  const prefill = (prompt: SavedPrompt) => {
    const current = chatbotStore.draftMessage ?? ''
    if (current.trim() === '') {
      apply(prompt)
      return
    }
    if (current === prompt.body) return
    confirm.require({
      header: t('chat.savedPrompts.replaceDraft.header'),
      message: t('chat.savedPrompts.replaceDraft.message', { name: prompt.name }),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
      acceptProps: { label: t('chat.savedPrompts.replaceDraft.accept') },
      accept: () => apply(prompt),
    })
  }

  return { prefill }
}
